import React, { useEffect, useRef, useState } from 'react';
import {
  Card,
  Form,
  Spin,
  Typography,
  Radio,
  Space,
  Row,
  Col,
  Input,
  Button,
  Divider,
  message,
  InputNumber,
} from 'antd';
import AriaDatePicker from 'components/AriaDatePicker';
import { ADD_NAVY_HCC_STEPS, ADD_AIR_FORCE_HCC_STEPS } from 'constants';
import useProviderEditDisabled from 'hooks/useProviderEditDisabled';
import { actions as centerActions } from 'features/add-center';
import { providerService } from 'services';
import { sleep, getPastDateValidator } from 'utils';
import { useDispatch } from 'react-redux';
import { PROVIDER_APPLICATION_PROGRAM_TYPES } from 'containers/Provider/SelectProgram/SelectProgram';
import { DatePickerFormItemLabel } from 'components/Form';

const { Title } = Typography;

const HCCResidents = ({
  application,
  setApplication,
  id,
  setStep,
  isFormSubmitting,
  loading,
  applicationSubmitted,
}) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();

  const adultResidentsPresent = Form.useWatch('adultResidentsPresent', form);
  const childResidentsPresent = Form.useWatch('childResidentsPresent', form);
  const formChildResidentsValue = form.getFieldValue('childResidents');
  const formAdultResidentsValue = form.getFieldValue('residents');

  const isSubmitDisabled = useProviderEditDisabled();
  const isAirForce = application?.programType.id === PROVIDER_APPLICATION_PROGRAM_TYPES.AIR_FORCE_HCC.id;

  // Keeps track of whether `next` is pressed or `save`
  const shouldGoToNextStep = useRef(false);

  const [dataLoading, setDataLoading] = useState(true);
  const fetchResidentData = async () => {
    try {
      const residents = await providerService.getResidents(id);
      const childResidents = await providerService.getChildResidents(id);
      form.setFieldsValue({
        residents: residents || [{}],
        childResidents: childResidents || [{}],
      });
    } catch (error) {
      console.error('Error fetching staff data', error);
    } finally {
      setDataLoading(false);
    }
  };

  useEffect(() => {
    fetchResidentData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    form.setFieldsValue({
      adultResidentsPresent: application.adultResidentsPresent,
      childResidentsPresent: application.childResidentsPresent,
    });
  }, [application.adultResidentsPresent, application.childResidentsPresent, form]);

  // If the user answers yes to childResidentsPresent
  // add an initial child resident item
  useEffect(() => {
    if ((childResidentsPresent && !formChildResidentsValue) || formChildResidentsValue?.length === 0) {
      form.setFieldsValue({
        childResidents: [{}],
      });
    }
  }, [childResidentsPresent, form, formChildResidentsValue]);

  // If the user answers yes to residentsPresent
  // add an initial resident item
  useEffect(() => {
    if ((adultResidentsPresent && !formAdultResidentsValue) || formAdultResidentsValue?.length === 0) {
      form.setFieldsValue({
        residents: [{}],
      });
    }
  }, [adultResidentsPresent, form, formAdultResidentsValue]);

  const onRemoveResident = async (residentId, residentType) => {
    try {
      if (residentType === 'residents') {
        await providerService.deleteResident(id, residentId);
      } else {
        await providerService.deleteChildResident(id, residentId);
      }
    } catch (err) {
      newrelic.noticeError(err);
      message.error('Unable to delete resident');
    }
  };

  return (
    <div id="residents" data-testid="residents">
      <Spin spinning={loading || dataLoading}>
        <Form
          layout="vertical"
          scrollToFirstError
          form={form}
          onFinish={async (values) => {
            try {
              const { residents, childResidents, adultResidentsPresent, childResidentsPresent } = values;
              dispatch(centerActions.setIsFormSubmitting(true));
              const updatedCenterData = await providerService.updateApplication(id, {
                ...application,
                adultResidentsPresent: adultResidentsPresent,
                childResidentsPresent: childResidentsPresent,
              });
              setApplication(updatedCenterData);

              //Add/update adult residents
              if (adultResidentsPresent && residents?.length !== 0) {
                for (const resident of residents) {
                  await providerService.addUpdateResidents(id, { ...resident, application: { id } });
                }
              } else if (adultResidentsPresent === false && updatedCenterData.residents?.length !== 0) {
                // Delete all adult residents if the user selects no
                for (const resident of updatedCenterData.residents) {
                  await providerService.deleteResident(id, resident.id);
                }
              }

              // Add/update child residents
              if (childResidentsPresent && childResidents?.length !== 0) {
                for (const child of childResidents) {
                  await providerService.addUpdateChildResidents(id, { ...child, application: { id } });
                }
              } else if (childResidentsPresent === false && updatedCenterData.childResidents?.length !== 0) {
                // Delete all child residents if the user selects no
                for (const child of updatedCenterData.childResidents) {
                  await providerService.deleteChildResident(id, child.id);
                }
              }

              if (shouldGoToNextStep.current) {
                setStep(isAirForce ? ADD_AIR_FORCE_HCC_STEPS.INSURANCE : ADD_NAVY_HCC_STEPS.AGREEMENT);
              } else {
                fetchResidentData();
              }

              message.success('Saved');
              await sleep(100);
            } catch (err) {
              newrelic.noticeError(err);
              message.error('Unable to save center details.');
            } finally {
              dispatch(centerActions.setIsFormSubmitting(false));
            }
          }}
          disabled={applicationSubmitted}
        >
          <Title>Residents</Title>
          <Card
            title={
              <Title level={2} className="mt-4">
                Adult residents
              </Title>
            }
            className="mb-4"
            data-testid="adultResidents"
          >
            <Form.Item
              name="adultResidentsPresent"
              label="do any adults over the age of 18 live at the facility?"
              rules={[{ required: true, message: 'Selection is required' }]}
              data-testid="adultResidentsPresent"
              className="mb-0"
            >
              <Radio.Group data-testid="radioGroup">
                <Space direction="vertical">
                  <Radio value={true} data-testid="yesRadio">
                    Yes
                  </Radio>
                  <Radio value={false} data-testid="noRadio">
                    No
                  </Radio>
                </Space>
              </Radio.Group>
            </Form.Item>
            {adultResidentsPresent && (
              <div className="mt-4">
                <Form.List name="residents">
                  {(residents, { add, remove }) => {
                    return (
                      <>
                        {residents.map((field, index) => (
                          <Resident
                            field={field}
                            index={index}
                            form={form}
                            remove={remove}
                            applicationSubmitted={applicationSubmitted}
                            adultOrChild="adult"
                            onRemoveResident={onRemoveResident}
                          />
                        ))}

                        <Divider />

                        <Button
                          onClick={() => {
                            add();
                          }}
                          style={{ borderRadius: '2px' }}
                          data-testid="addButton-adult"
                        >
                          Add adult resident
                        </Button>
                      </>
                    );
                  }}
                </Form.List>
              </div>
            )}
          </Card>

          <Card
            title={
              <Title level={2} className="mt-4">
                Child residents
              </Title>
            }
            className="mb-4"
          >
            <Form.Item
              name="childResidentsPresent"
              label="do any children under the age of 8 live at the facility?"
              rules={[{ required: true, message: 'Selection is required' }]}
              data-testid="childResidentsPresent"
              className="mb-0"
            >
              <Radio.Group data-testid="radioGroup">
                <Space direction="vertical">
                  <Radio value={true} data-testid="yesRadio-child">
                    Yes
                  </Radio>
                  <Radio value={false} data-testid="noRadio-child">
                    No
                  </Radio>
                </Space>
              </Radio.Group>
            </Form.Item>

            {childResidentsPresent && (
              <div className="mt-4">
                <Form.List name="childResidents">
                  {(childResidents, { add, remove }) => {
                    return (
                      <>
                        {childResidents.map((field, index) => (
                          <Resident
                            field={field}
                            index={index}
                            form={form}
                            remove={remove}
                            applicationSubmitted={applicationSubmitted}
                            adultOrChild="child"
                            onRemoveResident={onRemoveResident}
                          />
                        ))}
                        <Divider />
                        <Button
                          onClick={() => {
                            add();
                          }}
                          style={{ borderRadius: '2px' }}
                          data-testid="addButton-child"
                        >
                          Add child resident
                        </Button>
                      </>
                    );
                  }}
                </Form.List>
              </div>
            )}
          </Card>

          <div className="flex space-x-2">
            <Button
              onClick={() => setStep(ADD_NAVY_HCC_STEPS.STAFF_MEMBERS)}
              style={{ borderRadius: '0px' }}
              aria-label="back button"
              disabled={false}
              data-testod="backButton"
            >
              Back
            </Button>

            <div className="flex-grow"></div>

            {!applicationSubmitted && (
              <Button
                style={{ borderRadius: '0px' }}
                htmlType="submit"
                onClick={() => {
                  shouldGoToNextStep.current = false;
                }}
                disabled={isFormSubmitting || isSubmitDisabled}
                loading={isFormSubmitting}
                aria-label="save button"
              >
                Save
              </Button>
            )}

            <Button
              style={{ borderRadius: '0px' }}
              type="primary"
              role="button"
              htmlType={!applicationSubmitted ? 'submit' : undefined}
              onClick={() => {
                if (applicationSubmitted) {
                  setStep(isAirForce ? ADD_AIR_FORCE_HCC_STEPS.INSURANCE : ADD_NAVY_HCC_STEPS.AGREEMENT);
                  return;
                }
                shouldGoToNextStep.current = true;
              }}
              disabled={isFormSubmitting || isSubmitDisabled}
              loading={isFormSubmitting}
              data-testid="residents-form-submit"
              aria-label="next button"
            >
              Next
            </Button>
          </div>
        </Form>
      </Spin>
    </div>
  );
};

const Resident = ({ index, remove, field, form, applicationSubmitted, adultOrChild, onRemoveResident }) => {
  const handleRemove = () => {
    const residentType = adultOrChild === 'adult' ? 'residents' : 'childResidents';
    const resident = form.getFieldValue([residentType, index]);

    if (resident?.id) {
      onRemoveResident(resident.id, residentType);
    }
    remove(field.name);
  };

  return (
    <div data-testid="residentForm">
      {index !== 0 && <Divider />}
      <Title level={3}>{`${adultOrChild === 'child' ? 'Child' : 'Adult'} resident ${index + 1}`}</Title>
      <div className="hidden">
        <Form.Item name={[field.name, 'person', 'id']}>
          <InputNumber />
        </Form.Item>
      </div>
      <Row gutter={[20, 0]} className="form-row">
        <Col xs={24} sm={22} md={12}>
          <Form.Item
            label="first name"
            name={[field.name, 'person', 'firstName']}
            rules={[{ required: true, message: 'First name is required' }]}
            data-testid={`firstName-${adultOrChild}`}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} sm={22} md={12}>
          <Form.Item
            label="last name"
            name={[field.name, 'person', 'lastName']}
            rules={[{ required: true, message: 'Last name is required' }]}
            data-testid={`lastName-${adultOrChild}`}
          >
            <Input />
          </Form.Item>
        </Col>
      </Row>
      <Col xs={18} sm={16} md={6}>
        <Form.Item
          label={<DatePickerFormItemLabel title="Date of birth" />}
          name={[field.name, 'dateOfBirth']}
          rules={[
            {
              required: true,
              message: 'Date of birth is required',
            },
            getPastDateValidator('Date of birth should be in the past.'),
          ]}
          data-testid={`dateOfBirth-${adultOrChild}`}
        >
          <AriaDatePicker disabled={applicationSubmitted} />
        </Form.Item>
        {adultOrChild === 'adult' && (
          <Form.Item
            label="relationship to provider"
            name={[field.name, 'relationship']}
            rules={[{ required: true, message: 'relationship is required' }]}
            data-testid="relationship"
          >
            <Input />
          </Form.Item>
        )}
      </Col>

      {index > 0 && (
        <Button className="mb-4" onClick={handleRemove} style={{ borderRadius: '2px' }} data-testid="removeButton">
          Remove
        </Button>
      )}
    </div>
  );
};

export default HCCResidents;
